import { CdkDragStart } from '@angular/cdk/drag-drop';
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TransformedFiles } from '@core/models/designTool';
import { selectMobileView } from '@core/state/design-tool/design-tool.selectors';
import { Store } from '@ngrx/store';
import { PopConfirmComponent } from '@shared/components/pop-confirm/pop-confirm.component';
import { MdbPopconfirmService } from 'mdb-angular-ui-kit/popconfirm';
import { debounceTime, distinctUntilChanged, firstValueFrom, map, Subject, takeUntil, take } from 'rxjs';

@Component({
  selector: 'product-design-library',
  templateUrl: './product-design-library.component.html',
  styleUrls: ['./product-design-library.component.scss'],
})
export class ProductDesignLibraryComponent implements OnDestroy, OnChanges, AfterViewInit {
  searchTerm = new FormControl();
  mobileView: boolean;
  filteredFiles: TransformedFiles[] = [];
  destroy$ = new Subject()
  @Input() designFiles: TransformedFiles[] = [];
  @Output() uploadArtEvent = new EventEmitter<File[]|string>();
  @Output() selectedDesignFile = new EventEmitter<{ files: TransformedFiles[], coords?: { left: number; top: number}}>();
  @Output() removeDesignFile = new EventEmitter<string>();
  @Output() close = new EventEmitter<TransformedFiles[]>();
  draggingDesignFile: boolean;

  constructor(private store: Store, private popConfirmService: MdbPopconfirmService) {
    this.getMobileView()
    this.filteredFiles = this.designFiles
    this.searchTerm.valueChanges.pipe(debounceTime(1000), distinctUntilChanged(), takeUntil(this.destroy$), map(text => {
      this.filteredFiles = this.designFiles.filter(e => e.name.toLowerCase().includes(text.toLowerCase()))
    })).subscribe()
  }

  ngAfterViewInit(): void {
    this.filteredFiles = this.designFiles
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.designFiles.currentValue != changes.designFiles.previousValue) {
      this.filteredFiles = this.designFiles.map(e => {
        e['selected'] = false
        return e
      })
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(null)
    this.destroy$.complete()
  }

  async getMobileView() {
    this.mobileView = await firstValueFrom(this.store.select(selectMobileView))
  }

  /**
   * Open Upload Art Modal Event Emiiter
   */
  openUploadArtEvent(data?: File[]){
    this.uploadArtEvent.emit(data ?? 'uploadArt');
  }

  /**
   * Emit the selected image object to the parent component
   * @param image image object from the image library component
   */
  onImageSelect(image: TransformedFiles){
    if (!this.mobileView) {
      this.selectedDesignFile.emit({files: [image]})
    } else {
      image['selected'] = !image['selected']
    }
  }

  trackFiles(file: any) {
    return file?.name
  }

  get selectedFiles() {
    return this.filteredFiles.filter(e => e['selected'])
  }

  addSelectedFiles() {
    this.close.emit(this.selectedFiles)
  }

  onRemoveImage(event: any, file: TransformedFiles) {
    const target = event.target as HTMLElement;
    const popConfirmRef = this.popConfirmService.open(PopConfirmComponent, target, { popconfirmMode: 'modal', data: {
      title: 'Are you sure?',
      message: "You are about to remove a design from your designs library. This change can't be undone"
    } });
    popConfirmRef.onConfirm.pipe(take(1)).subscribe(_ => {
      const fileName = file.id.slice(file.id.lastIndexOf('/') + 1)
      this.removeDesignFile.emit(fileName)
    })
  }

  dragOverHandler(ev: any) {
    // Prevent default behavior (Prevent file from being opened)
    ev.preventDefault();
  }

  onDrop(event: any) {
    // Prevent default behavior (Prevent file from being opened)
    event.preventDefault();
    if (this.draggingDesignFile) return


    if (event.dataTransfer.items) {
      // Use DataTransferItemList interface to access the file(s)
      this.openUploadArtEvent([...event.dataTransfer.items].filter(e => e.kind === "file").map(e => e.getAsFile()))
    } else {
      // Use DataTransfer interface to access the file(s)
      this.openUploadArtEvent([...event.dataTransfer.files])
    }
  }

  onDragStart(event: DragEvent, item: TransformedFiles) {
    this.draggingDesignFile = true
    // return
    const dragElement = event.target;
    const pointerX = event.clientX//event.event['pageX']|event.event['screenX']|event.event['x']
    const pointerY = event.clientY//event.event['pageY']|event.event['screenY']|event.event['y']
    
    // Calculate the offset between the cursor and the top-left corner of the dragged element
    const offsetX = dragElement['x'] - pointerX;
    const offsetY = dragElement['y'] - pointerY;
    // Calculate the offset between the cursor and the top-left corner of the dragged element
    const dragOffset = {
      x: offsetX,
      y: offsetY
    };
    item['dragOffset'] = dragOffset
    item['width'] = dragElement['clientWidth']
    item['height'] = dragElement['clientWidth']
    item['imageWidth'] = dragElement['naturalWidth']
    item['imageHeight'] = dragElement['naturalHeight']
    event.dataTransfer.setData('design', JSON.stringify(item))
  }

  onDragEnd(event: DragEvent, item: TransformedFiles) {
    this.draggingDesignFile = false
  }
}
